import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import ProspectCard from './ProspectCard';
import ProspectDetailPanel from './ProspectDetailPanel';
import type { Prospect } from './ProspectCard';

const COLUMNS = [
  { id: 'prospect', label: 'Prospect', color: 'border-t-muted-foreground' },
  { id: 'contacted', label: 'Contacted', color: 'border-t-blue-500' },
  { id: 'in_conversation', label: 'In Conversation', color: 'border-t-amber-500' },
  { id: 'follow_up', label: 'Follow Up', color: 'border-t-orange-500' },
  { id: 'won', label: 'Won', color: 'border-t-emerald-500' },
  { id: 'lost', label: 'Lost', color: 'border-t-red-400' },
];

interface KanbanBoardProps {
  prospects: Prospect[];
  onRefresh: () => void;
  filterAssignee: string;
}

const KanbanBoard = ({ prospects, onRefresh, filterAssignee }: KanbanBoardProps) => {
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const filtered = filterAssignee
    ? prospects.filter((p) => p.assigned_to === filterAssignee)
    : prospects;

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    const id = e.dataTransfer.getData('text/plain');
    const prospect = prospects.find((p) => p.id === id);
    if (!prospect || prospect.status === newStatus) return;

    const now = ['contacted', 'in_conversation', 'follow_up'].includes(newStatus) ? new Date().toISOString() : undefined;
    await supabase.from('prospects').update({
      status: newStatus as any,
      ...(now ? { last_contacted_at: now } : {}),
    }).eq('id', id);

    await supabase.from('prospect_activities').insert({
      prospect_id: id,
      type: 'status_change',
      content: `Status changed to ${COLUMNS.find(c => c.id === newStatus)?.label}`,
    });

    onRefresh();
  };

  const handleDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    setDragOverColumn(colId);
  };

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-4 min-h-[calc(100vh-140px)]">
        {COLUMNS.map((col) => {
          const items = filtered.filter((p) => p.status === col.id);
          return (
            <div
              key={col.id}
              onDrop={(e) => handleDrop(e, col.id)}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={() => setDragOverColumn(null)}
              className={`flex-shrink-0 w-64 bg-muted/30 rounded-xl border-t-4 ${col.color} flex flex-col transition-colors ${
                dragOverColumn === col.id ? 'bg-muted/60' : ''
              }`}
            >
              <div className="p-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">{col.label}</h3>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                {items.map((p) => (
                  <ProspectCard
                    key={p.id}
                    prospect={p}
                    onClick={() => setSelectedProspect(p)}
                    onDragStart={handleDragStart}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selectedProspect && (
        <ProspectDetailPanel
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onUpdate={() => { onRefresh(); }}
        />
      )}
    </>
  );
};

export default KanbanBoard;
